import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../lib/utils';
import { Badge } from '../../../ui/badge';
import IconWrapper from '../../../ui/IconWrapper';
import { FiFileText, FiCircle } from 'react-icons/fi';

/**
 * RecordStatusBadge - Displays the workflow status of a record
 *
 * Simplified status system:
 * - draft: Record is being created/edited, not yet submitted
 * - open: Record is active and available for processing
 */

const statusConfig = {
  draft: {
    icon: FiFileText,
    label: 'Draft',
    variant: 'outline' as const,
    className: 'text-muted-foreground border-muted-foreground/30',
  },
  open: {
    icon: FiCircle,
    label: 'Open',
    variant: 'default' as const,
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  },
} as const;

export type RecordWorkflowStatus = keyof typeof statusConfig;

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 font-medium',
  {
    variants: {
      size: {
        sm: 'text-xs px-2 py-0.5',
        md: 'text-sm px-2.5 py-1',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  }
);

export interface RecordStatusBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** The workflow status of the record */
  status: RecordWorkflowStatus | string;
  /** Whether to show the icon */
  showIcon?: boolean;
}

const RecordStatusBadge = React.forwardRef<HTMLDivElement, RecordStatusBadgeProps>(
  ({ className, status, size, showIcon = true, ...props }, ref) => {
    // Get config for the status, fallback to 'open' for unknown statuses
    const normalizedStatus = status?.toLowerCase() as RecordWorkflowStatus;
    const config = statusConfig[normalizedStatus] || statusConfig.open;

    return (
      <Badge
        ref={ref}
        variant={config.variant}
        className={cn(
          badgeVariants({ size }),
          config.className,
          className
        )}
        {...props}
      >
        {showIcon && (
          <IconWrapper
            icon={config.icon}
            className="h-3 w-3"
          />
        )}
        {config.label}
      </Badge>
    );
  }
);

RecordStatusBadge.displayName = 'RecordStatusBadge';

export { RecordStatusBadge };
export default RecordStatusBadge;
